'use client';

import { c as _c } from "react/compiler-runtime";
import type { TFunction } from 'i18next';
import { useSearchParams } from 'next/navigation';
import { Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { ActionCard } from '@sola/ark-core/molecules/action-card/action-card.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { ChildImageVariantEnum, getChildImageUrlBase } from '@sola/ark-core/utils/child-image.ts';
import { useRouter } from '@sola/ark-core/utils/hooks/useRouter.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { Image } from '@sola/ark-skippy/atoms/image/image.tsx';
import { Lightbox, LightboxTrigger } from '@sola/ark-skippy/molecules/lightbox/lightbox.tsx';
import { useChildDetails } from '@sola/my-sdk/children/hooks.ts';
import type { ChildDetails } from '@sola/schema/dotcom/children/child.ts';
import type { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import type { BlockProps } from '@sola/schema/lib/schema.ts';
import loader from '@sola/shared-utils/ark-skippy/loader.ts';
import { getAgeFromBirthday } from '@sola/shared-utils/children/utils.ts';
import { useSponsorships } from '@sola/supporter-sdk/hooks/commitments.ts';
import { BackButton } from '../../atoms/back-button/back-button.tsx';
import { ChildBioAccordion } from '../../molecules/child-bio-accordion/child-bio-accordion.tsx';
import { SponsorshipsSkeleton } from '../sponsorships-list/sponsorships-skeleton.tsx';
interface ChildBioHeadingProps {
  preferredName: string;
  age: string | number;
  id: string;
}
const ChildBioHeading: React.FC<ChildBioHeadingProps> = t0 => {
  const $ = _c(11);
  const {
    preferredName,
    age,
    id
  } = t0;
  let t1;
  if ($[0] !== preferredName) {
    t1 = <h1 className="heading-sm heading-neutral-amplified mb-snug-lg md:heading-md">{preferredName}</h1>;
    $[0] = preferredName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== age || $[3] !== id) {
    t2 = {
      age,
      id
    };
    $[2] = age;
    $[3] = id;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      ageLabel: <span className="label-potent" />,
      idLabel: <span className="label-potent" />
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2) {
    t4 = <div className="body-sm label-neutral-mild"><IntlBlock i18nKey="sponsorshipRelationship.childBio.metadata" ns="my" values={t2} components={t3} /></div>;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t4) {
    t5 = <div>{t1}{t4}</div>;
    $[8] = t1;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};
interface ChildBioImageProps {
  child: ChildDetails;
}
const ChildBioImage: React.FC<ChildBioImageProps> = t0 => {
  const $ = _c(13);
  const {
    child
  } = t0;
  const {
    imageUrl,
    name: t1
  } = child;
  const {
    preferred: preferredName
  } = t1;
  let t2;
  if ($[0] !== imageUrl) {
    t2 = getChildImageUrlBase(imageUrl, ChildImageVariantEnum.Landscape) ?? "";
    $[0] = imageUrl;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const croppedChildImage = t2;
  let t3;
  if ($[2] !== imageUrl) {
    t3 = getChildImageUrlBase(imageUrl, ChildImageVariantEnum.Full);
    $[2] = imageUrl;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const fullChildImage = t3;
  const t4 = fullChildImage ?? imageUrl;
  let t5;
  if ($[4] !== preferredName || $[5] !== t4) {
    t5 = [{
      media: {
        image: {
          url: t4,
          alt: preferredName,
          height: 0,
          width: 0
        }
      }
    }];
    $[4] = preferredName;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== croppedChildImage || $[8] !== preferredName) {
    t6 = <LightboxTrigger><Image image={{
        alt: preferredName,
        url: croppedChildImage
      }} loader={loader} sizeIntent="full" aspectRatio="16-9" objectPosition="top" rounded="lg" /></LightboxTrigger>;
    $[7] = croppedChildImage;
    $[8] = preferredName;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = <Lightbox key="lightbox" items={t5}>{t6}</Lightbox>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
};
interface ChildBioSummaryProps {
  child: ChildDetails;
}
const ChildBioSummary: React.FC<ChildBioSummaryProps> = t0 => {
  const $ = _c(9);
  const {
    child
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["my"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  let t2;
  if ($[1] !== child.birthDate || $[2] !== child.country || $[3] !== child.gender || $[4] !== child.name.preferred || $[5] !== t) {
    t2 = t("sponsorshipRelationship.childBio.summary", {
      childName: child.name.preferred,
      age: getAgeFromBirthday(child.birthDate),
      country: child.country,
      context: child.gender
    });
    $[1] = child.birthDate;
    $[2] = child.country;
    $[3] = child.gender;
    $[4] = child.name.preferred;
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = <span className="body-md label-neutral-mild">{t2}</span>;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const getLetterCtaSubtitle = (commitment: GraphQLCommitment, t: TFunction<'my', undefined>) => {
  if (commitment.lastCorrespondenceDate) {
    return t('ctas.writeLetter.subtitle', {
      date: commitment.lastCorrespondenceDate
    });
  }
  return t('ctas.writeLetter.subtitle_fallback');
};
interface ChildBioCtaCardsProps {
  commitment: GraphQLCommitment;
}
const ChildBioCtaCards: React.FC<ChildBioCtaCardsProps> = t0 => {
  const $ = _c(24);
  const {
    commitment
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["my"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  let t2;
  if ($[1] !== t) {
    t2 = t("ctas.writeLetter.url");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ArkIcon className="text-accent-default" iconName="file-heart-01" size="1.5rem" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== commitment || $[5] !== t) {
    t4 = getLetterCtaSubtitle(commitment, t);
    $[4] = commitment;
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t) {
    t5 = t("ctas.writeLetter.title");
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t2 || $[10] !== t4 || $[11] !== t5) {
    t6 = <ActionCard className="flex-1" href={t2} icon={t3} subtitle={t4} title={t5} />;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t) {
    t7 = t("ctas.give.url");
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ArkIcon className="text-accent-default" iconName="gift-01" size="1.5rem" />;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t) {
    t9 = t("ctas.give.title");
    $[16] = t;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t7 || $[19] !== t9) {
    t10 = <ActionCard className="flex-1" href={t7} icon={t8} title={t9} />;
    $[18] = t7;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t6) {
    t11 = <div className="flex flex-col md:flex-row gap-comfy-sm w-full">{t6}{t10}</div>;
    $[21] = t10;
    $[22] = t6;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
};
const useComponentData = id => {
  const $ = _c(15);
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== id || $[1] !== searchParams) {
    t0 = id ?? searchParams.get("gid");
    $[0] = id;
    $[1] = searchParams;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const globalId = t0;
  const {
    data: child,
    isLoading: isChildLoading,
    isFetching: isChildFetching,
    isPending: isChildPending,
    isError: isChildError
  } = useChildDetails(globalId ?? undefined);
  const {
    sponsorships,
    isLoading: isSponsorshipsLoading,
    isFetching: isSponsorshipsFetching,
    isPending: isSponsorshipsPending,
    isError: isSponsorshipsError
  } = useSponsorships();
  let t1;
  if ($[3] !== globalId || $[4] !== sponsorships) {
    let t2;
    if ($[6] !== globalId) {
      t2 = c => c.participant?.globalId === globalId;
      $[6] = globalId;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    t1 = sponsorships.find(t2);
    $[3] = globalId;
    $[4] = sponsorships;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const commitment = t1;
  const isLoading = isChildLoading || isSponsorshipsLoading;
  const isFetching = isChildFetching || isSponsorshipsFetching;
  const isPending = isChildPending || isSponsorshipsPending;
  const isError = isChildError || isSponsorshipsError;
  const isShowingSkeletons = (isLoading || isFetching || isPending) && !isError;
  const isShowingChild = !isShowingSkeletons && !!child && !!commitment;
  const isShowingError = !isShowingSkeletons && (isError || !child && !!globalId);
  const sponsorshipCount = sponsorships?.length;
  let t2;
  if ($[8] !== child || $[9] !== commitment || $[10] !== isShowingChild || $[11] !== isShowingError || $[12] !== isShowingSkeletons || $[13] !== sponsorshipCount) {
    t2 = {
      child,
      commitment,
      sponsorshipCount,
      isShowingSkeletons,
      isShowingChild,
      isShowingError
    };
    $[8] = child;
    $[9] = commitment;
    $[10] = isShowingChild;
    $[11] = isShowingError;
    $[12] = isShowingSkeletons;
    $[13] = sponsorshipCount;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
};
type ChildBioProps = BlockProps & {
  id?: string;
};
const ChildBioContent: React.FC<ChildBioProps> = ({
  id
}) => {
  const {
    t
  } = useTranslation(['my']);
  const {
    usePathname
  } = useRouter();
  // eslint-disable-next-line react-compiler/react-compiler -- usePathname is a framework-agnostic hook passed via RouterContext
  const pathname = usePathname();
  const backButtonUrl = pathname.replace(/\/$/, '').replace(/\/[^/]+$/, '');
  const {
    child,
    commitment,
    sponsorshipCount,
    isShowingSkeletons,
    isShowingChild,
    isShowingError
  } = useComponentData(id);
  if (isShowingSkeletons) {
    return <SponsorshipsSkeleton />;
  }
  return <section className="flex flex-col gap-comfy-lg component--container component relative max-w-md">
      {sponsorshipCount > 1 && backButtonUrl && <BackButton url={backButtonUrl} label={t('sponsorshipRelationship.childBio.backButton.text')} />}

      {isShowingChild && child && commitment && <div className="flex flex-col gap-comfy-xl">
          <ChildBioHeading preferredName={child.name.preferred} age={getAgeFromBirthday(child.birthDate)} id={child.localId} />

          <ChildBioImage child={child} />

          <ChildBioSummary child={child} />

          <ChildBioCtaCards commitment={commitment} />
          <ChildBioAccordion child={child} commitment={commitment} />
        </div>}

      {isShowingError && <Alert variant="negative" title={t('sponsorshipRelationship.childBio.alert.title')} message={t('sponsorshipRelationship.childBio.alert.message')} action={<a href={t('my:ctas.contactSupport.url')}>
              {t('my:ctas.contactSupport.text')}
            </a>} />}
    </section>;
};
export const ChildBio: React.FC<ChildBioProps> = t0 => {
  const $ = _c(3);
  const {
    id
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SponsorshipsSkeleton />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== id) {
    t2 = <Suspense fallback={t1}><ChildBioContent id={id} /></Suspense>;
    $[1] = id;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};